import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, mergeMap, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { userNotificationsActions } from '../actions/user-notifications.actions';
import { UserNotificationsService } from '../services/user-notifications.service';
import { NotificationsSocketService } from '../services/notifications-socket.service';
import { AumicoCoreConfig, AumicoCoreConfigService } from '../aumico-core.config';

@Injectable()
export class UserNotificationsEffects {
    private readonly socketEnabled: boolean;

    constructor(
        private readonly actions$: Actions,
        private readonly service: UserNotificationsService,
        private readonly socketService: NotificationsSocketService,
        @Inject(AumicoCoreConfigService) config: AumicoCoreConfig,
    ) {
        this.socketEnabled = config?.enableNotificationsSocket !== false;
    }

    loadUserNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userNotificationsActions.loadUserNotifications),
            mergeMap(({ request }) =>
                this.service.getUserNotifications$(request).pipe(
                    map((response) =>
                        userNotificationsActions.loadUserNotificationsSuccess({
                            notifications: response.notifications,
                            unreadCount: response.unreadCount,
                            offset: request.offset,
                            limit: request.limit,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(userNotificationsActions.loadUserNotificationsFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    connectSocket$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(userNotificationsActions.connectNotificationsSocket),
                tap(({ token }) => {
                    if (!this.socketEnabled) return;
                    this.socketService.connect(token);
                }),
            ),
        { dispatch: false },
    );

    disconnectSocket$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(userNotificationsActions.disconnectNotificationsSocket),
                tap(() => this.socketService.disconnect()),
            ),
        { dispatch: false },
    );

    socketNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userNotificationsActions.connectNotificationsSocket),
            switchMap(() => {
                if (!this.socketEnabled) return EMPTY;
                return this.socketService.notification$.pipe(
                    map((notification) => userNotificationsActions.notificationReceived({ notification })),
                    takeUntil(this.actions$.pipe(ofType(userNotificationsActions.disconnectNotificationsSocket))),
                );
            }),
        ),
    );

    updateUserNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userNotificationsActions.updateNotification),
            mergeMap(({ notificationIds, update }) =>
                this.service.updateNotifications$(notificationIds, update).pipe(
                    map((response) =>
                        userNotificationsActions.updateNotificationSuccess({
                            notifications: response.notifications,
                            unreadCount: response.unreadCount,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(userNotificationsActions.updateNotificationFailure({ error })),
                    ),
                ),
            ),
        ),
    );
}
